/**
 * Free-play endless: full combat snapshots at stable protocol boundaries.
 *
 * - Saved when a Protocol starts / advances and integrity is healthy
 * - Only the farthest stable checkpoint per difficulty is kept
 * - Resume restores board, hand, deck, time, and escalation — not a soft warm start
 */
import type { BoardState } from "./board/board";
import type { Difficulty } from "./board/types";
import type { EscalationRuntime } from "./escalation";
import type { ResourceState } from "./resources";
import { formatSurviveTime } from "./endlessScore";

export const ENDLESS_CHECKPOINT_KEY = "sots-endless-checkpoint-v1";

/** Minimum integrity fraction required to keep a protocol snapshot. */
export const CHECKPOINT_MIN_INTEGRITY_FRAC = 0.25;

export type EndlessCheckpointPayload = {
  v: 1;
  scenarioId: string;
  difficulty: Difficulty;
  /** ISO time when this slot was written */
  savedAt: string;
  elapsed: number;
  waveLabel: string;
  resources: ResourceState;
  /**
   * Library deck this run started from. Missing on pre-tag snapshots.
   * Resume is offered only while this deck’s saved cards still match
   * `loadoutKey` — mid-run gifts live on `deckPool`, not the fingerprint.
   */
  deckId?: string;
  /** Sorted multiset of the saved loadout at run start. */
  loadoutKey?: string;
  deckPool: string[];
  /** Draw pile (top = end of array, same as Game.deck) */
  deck: string[];
  discardPile: string[];
  /** Hand verse def ids (left → right) */
  hand: string[];
  board: BoardState;
  firedSpawns: number[];
  escalation: EscalationRuntime;
  empowerCharges: number;
  empowerBonusSteps: number;
  speakHasteCharges: number;
  speakHasteFactor: number;
  joyField: {
    remaining: number;
    wearPerSec: number;
    onlyTypes: string[];
    slowFactor: number;
    maxStepsPerLie: number;
    stepsDone: [string, number][];
    reference: string;
    defId: string;
  } | null;
  presenceSeq: number;
  cardSeq: number;
  lastMessage: string;
};

type Store = {
  byDifficulty: Partial<Record<string, EndlessCheckpointPayload>>;
};

function loadStore(): Store {
  try {
    const raw = localStorage.getItem(ENDLESS_CHECKPOINT_KEY);
    if (!raw) return { byDifficulty: {} };
    const parsed = JSON.parse(raw) as Store;
    if (!parsed || typeof parsed !== "object") return { byDifficulty: {} };
    return { byDifficulty: parsed.byDifficulty ?? {} };
  } catch {
    return { byDifficulty: {} };
  }
}

function writeStore(store: Store): void {
  try {
    localStorage.setItem(ENDLESS_CHECKPOINT_KEY, JSON.stringify(store));
  } catch {
    /* quota */
  }
}

export function getEndlessCheckpoint(
  difficulty: string,
): EndlessCheckpointPayload | null {
  const cp = loadStore().byDifficulty[difficulty];
  if (!cp || cp.v !== 1) return null;
  if (typeof cp.elapsed !== "number" || cp.elapsed < 0) return null;
  return cp;
}

export function hasEndlessCheckpoint(difficulty: string): boolean {
  return !!getEndlessCheckpoint(difficulty);
}

/**
 * Keep only the farthest stable checkpoint per difficulty.
 * Returns true if the store was updated.
 */
export function saveEndlessCheckpoint(
  payload: EndlessCheckpointPayload,
): boolean {
  if (payload.v !== 1) return false;
  if (payload.elapsed < 1) return false;
  const maxI = Math.max(1, payload.resources.maxIntegrity);
  if (payload.resources.integrity / maxI < CHECKPOINT_MIN_INTEGRITY_FRAC) {
    return false;
  }

  const store = loadStore();
  const prev = store.byDifficulty[payload.difficulty];
  // Prefer farther depth; ties keep the newer write
  if (prev && prev.elapsed > payload.elapsed + 0.5) return false;

  store.byDifficulty[payload.difficulty] = {
    ...payload,
    savedAt: new Date().toISOString(),
  };
  writeStore(store);
  return true;
}

export function clearEndlessCheckpoint(difficulty: string): void {
  const store = loadStore();
  if (!store.byDifficulty[difficulty]) return;
  delete store.byDifficulty[difficulty];
  writeStore(store);
}

/** Multiset fingerprint — order does not matter, extra copies do. */
export function loadoutKey(ids: readonly string[]): string {
  return [...ids].sort().join("\0");
}

/**
 * True when this snapshot still belongs to `deck` as it is saved now.
 * Untagged (legacy) snapshots match any deck until a save retires them.
 */
export function checkpointMatchesLoadout(
  cp: EndlessCheckpointPayload,
  deck: { id: string; verseIds: readonly string[] },
): boolean {
  if (!cp.deckId || !cp.loadoutKey) return true;
  return cp.deckId === deck.id && cp.loadoutKey === loadoutKey(deck.verseIds);
}

/** Checkpoint to offer as Resume for this difficulty + current loadout. */
export function getResumeCheckpoint(
  difficulty: string,
  deck: { id: string; verseIds: readonly string[] },
): EndlessCheckpointPayload | null {
  const cp = getEndlessCheckpoint(difficulty);
  if (!cp || !checkpointMatchesLoadout(cp, deck)) return null;
  return cp;
}

/**
 * Drop snapshots started from this library deck.
 * Untagged legacy snapshots are also dropped — they cannot prove they
 * belong to another loadout.
 */
export function clearEndlessCheckpointsForDeck(deckId: string): void {
  if (!deckId) return;
  const store = loadStore();
  let changed = false;
  for (const key of Object.keys(store.byDifficulty)) {
    const cp = store.byDifficulty[key];
    if (!cp) continue;
    if (!cp.deckId || cp.deckId === deckId) {
      delete store.byDifficulty[key];
      changed = true;
    }
  }
  if (changed) writeStore(store);
}

/** Short player-facing line for hub / end screen. */
export function formatCheckpointSummary(
  cp: EndlessCheckpointPayload,
): string {
  const t = formatSurviveTime(cp.elapsed);
  const label = cp.waveLabel?.trim() || "Checkpoint";
  return `${label} · ${t}`;
}

export function checkpointIsStable(resources: ResourceState): boolean {
  const maxI = Math.max(1, resources.maxIntegrity);
  return resources.integrity / maxI >= CHECKPOINT_MIN_INTEGRITY_FRAC;
}
